/**
 * Languages this build ships translations and date locales for.
 * `name` is the endonym, so the picker reads natively whatever the active UI language is.
 */
export const FALLBACK_LANGUAGES = [
    { code: 'sv', name: 'Svenska' },
    { code: 'en', name: 'English' }
];

export const DEFAULT_LANGUAGE = 'sv';

/** localStorage key. Matches the key i18next-browser-languagedetector would use. */
export const STORAGE_KEY = 'i18nextLng';

/**
 * Holds a language the user picked that the server has not accepted yet.
 *
 * GET /users/validate returns a resolved language without saying where it came from, so the client
 * cannot tell "this user chose Swedish" from "nobody has chosen anything, so here is the default".
 * Without this marker the server's default silently overwrites an unsaved choice on the next reload.
 */
export const PENDING_KEY = 'i18nextLngPending';

const SUPPORTED_CODES = FALLBACK_LANGUAGES.map(language => language.code);

/**
 * Reduces a BCP 47 tag to the ISO 639-1 code the API and our resource bundles use.
 * `navigator.language` yields `sv-SE` / `en-GB`, and the API rejects regional tags with a 400.
 * @param {string} tag
 * @returns {string|null} Two-letter code, or null if it is not a language we ship
 */
export function normaliseLanguage(tag) {
    if (typeof tag !== 'string') {
        return null;
    }

    const code = tag.trim().toLowerCase().split(/[-_]/)[0];

    return SUPPORTED_CODES.indexOf(code) !== -1 ? code : null;
}

function readStoredLanguage() {
    try {
        return normaliseLanguage(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
        // Safari in private mode throws on localStorage access.
        return null;
    }
}

function readBrowserLanguage() {
    const candidates = (window.navigator.languages || [window.navigator.language]).filter(Boolean);

    for (let i = 0; i < candidates.length; i++) {
        const code = normaliseLanguage(candidates[i]);

        if (code != null) {
            return code;
        }
    }

    return null;
}

/**
 * Stored choice, then browser preference, then Swedish.
 * @returns {string} ISO 639-1 code
 */
export function detectLanguage() {
    return readStoredLanguage() || readBrowserLanguage() || DEFAULT_LANGUAGE;
}

/**
 * @returns {string|null} a choice this device holds that the server has not accepted
 */
export function getPendingLanguage() {
    try {
        return normaliseLanguage(window.localStorage.getItem(PENDING_KEY));
    } catch (error) {
        return null;
    }
}

/**
 * @param {string} language ISO 639-1 code
 */
export function markLanguagePending(language) {
    const code = normaliseLanguage(language);

    if (code == null) {
        return;
    }

    try {
        window.localStorage.setItem(PENDING_KEY, code);
    } catch (error) {
        // Nothing to do; the language itself is still applied for this session.
    }
}

export function clearLanguagePending() {
    try {
        window.localStorage.removeItem(PENDING_KEY);
    } catch (error) {
        // Nothing to do.
    }
}

/**
 * Whether this device carries a language the user picked themselves. Guesses — the organisation
 * default, the browser — must never overwrite it.
 * @returns {boolean}
 */
export function hasStoredLanguage() {
    return readStoredLanguage() != null;
}

/**
 * @param {string} language ISO 639-1 code
 */
export function storeLanguage(language) {
    const code = normaliseLanguage(language);

    if (code == null) {
        return;
    }

    try {
        window.localStorage.setItem(STORAGE_KEY, code);
    } catch (error) {
        // Nothing we can do; the in-memory language is still correct for this session.
    }
}

/**
 * The language the UI is currently rendering in. Mirrored here — rather than read from the i18next
 * singleton — so that low-level modules such as base.external.js can ask for it without importing
 * i18next and creating a module cycle. src/i18n/index.js keeps it in step.
 */
let activeLanguage = DEFAULT_LANGUAGE;

/**
 * @returns {string} ISO 639-1 code, never a regional tag
 */
export function getActiveLanguage() {
    return activeLanguage;
}

/**
 * @param {string} language ISO 639-1 code
 */
export function setActiveLanguage(language) {
    const code = normaliseLanguage(language);

    if (code != null) {
        activeLanguage = code;
    }
}
